import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysSite } from './queryKeys';

type DeleteSiteIdentifierResponse = {
  code: string;
  message: string;
  requestId: UUID | undefined;
};

type DeleteSiteIdentifierArguments = {
  identifierUuid: UUID;
  siteId: UUID;
};

type DeleteSiteIdentifierMutationOptions = UseMutationOptions<
  DeleteSiteIdentifierResponse,
  Error,
  DeleteSiteIdentifierArguments
>;

/**
 * Deletes a given site identifier for a given site.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/remove_identifier_for_supplier_site__site_uuid__identifier__identifier_uuid__delete
 *
 * @param {string} siteId - The ID of the site.
 * @param {string} identifierUuid - The ID of the site identifier.
 * @return {Promise<DeleteSiteIdentifierResponse>} The response of the PUT request.
 */
export const deleteSiteIdentifier = async (
  siteId: UUID,
  identifierUuid: UUID,
) => {
  const response = await vestigasApi
    .delete(ENDPOINT.SITE.DELETE_IDENTIFIER(siteId, identifierUuid))
    .json<DeleteSiteIdentifierResponse>();

  return response;
};

/**
 * Custom hook for deleting a site identifier.
 *
 * @param {Object} options - Additional options for the mutation.
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationDeleteSiteIdentifier = (
  options?: DeleteSiteIdentifierMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      siteId,
      identifierUuid,
    }: DeleteSiteIdentifierArguments) =>
      deleteSiteIdentifier(siteId, identifierUuid),
    onSuccess(_, { siteId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysSite.get(siteId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeysSite.getAll({}),
      });
    },
    ...options,
  });
};
